import { BoxBorder, Color } from '@genial/design-tokens';
import { BasketCustomerOfferEntity } from '@genial/services';
import { Utils } from '@genial/utils';
import { ISkeletonProps, Skeleton } from 'native-base';

import { getValidatedStatusIcon } from '../../../../utils/getValidatedStatusIcon';
import { OrderFullStatus } from '../../../OrderHistoryModal/components/OrderFullStatus';

export enum MarketType {
  FixedIncome = 'FixedIncome',
  Equity = 'Equity',
  Fund = 'Fund',
  Treasury = 'Treasury',
  COE = 'COE',
}

export const MarketTypeTransform = {
  [MarketType.FixedIncome]: 'Renda Fixa',
  [MarketType.Equity]: 'Renda Variável',
  [MarketType.Fund]: 'Fundo',
  [MarketType.Treasury]: 'Tesouro Direto',
  [MarketType.COE]: 'Op. Estruturada',
};

export const ColoredSkeleton = (props: ISkeletonProps) => (
  <Skeleton
    {...props}
    endColor={Color['brand.primary.lightest']}
    w="40px"
    h="14px"
    borderRadius={BoxBorder.heavy}
  />
);

export const checkOrderIsEquity = (item: BasketCustomerOfferEntity) =>
  item.marketType === MarketType.Equity;

export const checkOrderIsStructuredProduct = (item: BasketCustomerOfferEntity) =>
  item.marketType === MarketType.COE;

export const checkOrderIsDirectTreasury = (item: BasketCustomerOfferEntity) =>
  item.marketType === MarketType.Treasury;

export const checkOrderIsFixedIncome = (item: BasketCustomerOfferEntity) =>
  item.marketType === MarketType.FixedIncome;

const getTranslatedMarketType = (marketType?: string | null) => {
  if (!marketType) {
    return '--';
  }
  return MarketTypeTransform[marketType] || '--';
};

const getValue = (value?: number | null, forceNumber = false) => {
  if (value === null || value === undefined) {
    return forceNumber ? 0 : '--';
  }
  return forceNumber ? value : `${value}`;
};

const getExecutedAveragePriceValue = (basket: BasketCustomerOfferEntity, withDetails: boolean) => {
  if (!withDetails) {
    return <ColoredSkeleton />;
  }

  if (!basket.order) {
    return '--';
  }

  return Utils.Mask.Money(basket.order?.executedAveragePrice || 0);
};

export const getBatchTicketDetailsItems = (
  basket: BasketCustomerOfferEntity,
  withDetails: boolean
) => {
  const productTypeDetail = {
    label: 'Produto',
    value: getTranslatedMarketType(basket.marketType),
  };

  const assetDetail = {
    label: 'Ativo',
    value: basket.offer.productDisplay || '--',
  };

  const requestedAmountDetail = {
    label: 'Valor enviado',
    value: Utils.Mask.Money(basket.requestedAmount),
  };

  const statusDetail = {
    label: 'Status',
    value: (
      <OrderFullStatus
        statusIcon={getValidatedStatusIcon(basket.basketOfferStatus.statusIcon)}
        statusMessage={basket.basketOfferStatus.statusAlias}
      />
    ),
  };

  const reasonDetail = {
    label: 'Motivo',
    value: withDetails ? (
      basket?.order?.error?.internalMessage || basket?.order?.error?.externalMessage || '--'
    ) : (
      <ColoredSkeleton />
    ),
  };

  const executedAmount = withDetails ? getValue(basket.order?.executedAmount) : <ColoredSkeleton />;

  const canceledQuantity = withDetails ? (
    getValue(basket.order?.canceledQuantity)
  ) : (
    <ColoredSkeleton />
  );

  const executedAveragePrice = getExecutedAveragePriceValue(basket, withDetails);

  if (checkOrderIsEquity(basket)) {
    return [
      productTypeDetail,
      assetDetail,
      { label: 'Operação', value: 'COMPRA' },
      { label: 'Tipo da ordem', value: basket.orderType || '--' },
      { label: 'Qtd. solicitada', value: `${basket.requestedQuantity}` },
      { label: 'Qtd. executada', value: executedAmount },
      { label: 'Qtd. cancelada', value: canceledQuantity },
      { label: 'Preço médio executado', value: executedAveragePrice },
    ];
  }

  if (checkOrderIsStructuredProduct(basket)) {
    return [
      { label: 'Operações', value: basket.offer.productDisplay || '--' },
      { label: 'Tipo de boletagem', value: basket.billingType?.billingDescription || '--' },
      requestedAmountDetail,
      statusDetail,
      reasonDetail,
    ];
  }

  if (checkOrderIsDirectTreasury(basket)) {
    return [
      assetDetail,
      { label: 'Quantidade', value: `${basket.quantity?.toFixed(2)}` || '--' },
      requestedAmountDetail,
      statusDetail,
      reasonDetail,
    ];
  }

  if (checkOrderIsFixedIncome(basket)) {
    const hasFeeDetail = !!basket.requestedOfferDetail?.customerFeeAmount;

    const defaultUnitPrice = basket.offer.unitPrice
      ? Utils.Mask.Money(basket.offer.unitPrice, {
          maximumFractionDigits: 8,
        })
      : '--';

    const customerFeeAmount = hasFeeDetail
      ? Utils.Mask.Percentage(basket.requestedOfferDetail?.customerFeeAmount)
      : basket.offer.profitabilityFormatted;
    const customerPUAmount = hasFeeDetail
      ? Utils.Mask.Money(basket.requestedOfferDetail?.customerPuAmount, {
          maximumFractionDigits: 8,
        })
      : defaultUnitPrice;

    return [
      assetDetail,
      { label: 'Taxa', value: customerFeeAmount || '--' },
      { label: 'Preço unitário', value: customerPUAmount },
      requestedAmountDetail,
      statusDetail,
      reasonDetail,
    ];
  }

  return [assetDetail, requestedAmountDetail, statusDetail, reasonDetail];
};

export const getBatchTicketPreDetailsItems = (basket: BasketCustomerOfferEntity) => {
  const hasInteractionChannelAudit =
    !!basket.interactionChannelAudit && !!basket.interactionChannelAudit.description;

  if (hasInteractionChannelAudit) {
    return [
      {
        label: 'Solicitação foi gravada?',
        value: basket.interactionChannelAudit.description || '--',
      },
      {
        label: 'Canal de solicitação',
        value: basket.interactionChannelAudit?.interactionChannelOption?.channel || '--',
      },
    ];
  }

  return [];
};

export const getBatchTicketExtraDetailsItems = (
  basket: BasketCustomerOfferEntity,
  withDetails: boolean
) => {
  if (!basket || !withDetails) {
    return [];
  }

  if (checkOrderIsEquity(basket)) {
    return (basket.orderEvents || []).map((event) => [
      {
        label: 'Qtd. aberta',
        value: `${event.openedQuantity}`,
      },
      {
        label: 'Qtd. executada',
        value: `${event.executedQuantity}`,
      },
      {
        label: 'Preço executado',
        value: Utils.Mask.Money(event.executedAmount),
      },
      {
        label: 'Últ. atualização',
        value: Utils.Mask.DateFormat(event.orderEventDate || undefined, 'dd/MM/yyyy HH:mm:ss'),
      },
      {
        label: 'Status',
        value: (
          <OrderFullStatus
            statusIcon={getValidatedStatusIcon(event.eventStatus.statusIcon)}
            statusMessage={event.eventStatus.status}
          />
        ),
      },
    ]);
  }

  return [];
};

export const getBatchTicketAssetsColumns = (
  basket: BasketCustomerOfferEntity,
  viewportType?: 'mobile' | 'tablet' | 'desktop'
) => {
  if (checkOrderIsEquity(basket)) {
    return 4;
  }
  if (checkOrderIsFixedIncome(basket)) {
    const fixedIncomeColumnsCount = {
      mobile: 1,
      tablet: 4,
      desktop: 6,
    };

    return fixedIncomeColumnsCount[viewportType || 'desktop'];
  }
};
